/**
 * RESCATE DE UNA POSICIÓN DESVALORIZADA: dónde queda el borde por el que sales con tu dinero.
 *
 * El caso: entraste con 1.000 $ en una pool de Uniswap, el precio se salió por abajo y te quedaste
 * con el token volátil — hoy valen 860 $. Traes ese volátil a Keel y dices que quieres recuperar
 * 1.000. La pregunta que hay que responder ANTES de firmar es sencilla y concreta: ¿a qué precio
 * tiene que volver para que eso ocurra?
 *
 * Y tiene solución cerrada, sin iterar. Una posición de Uniswap V3 con liquidez L en [a, b] (raíces
 * de precio) vale, cuando el precio la deja entera en un lado:
 *
 *      amount0 = L·(1/a − 1/b)        amount1 = L·(b − a)
 *
 * Si entras con SOLO el volátil, el precio está pegado a uno de los bordes y el otro es el de
 * salida. Eliminando L entre las dos ecuaciones:
 *
 *   · estable = token0 (el volátil es token1, se vende según BAJA el precio hacia `a`):
 *        objetivo = amt1/(a·b)   →   a = amt1/(objetivo·b)
 *   · estable = token1 (el volátil es token0, se vende según SUBE hacia `b`):
 *        objetivo = amt0·a·b     →   b = objetivo/(amt0·a)
 *
 * Es la misma cuenta que hace el contrato en `RangeMath`, resuelta aquí para poder enseñarla antes
 * de gastar gas. Se trabaja en unidades CRUDAS (con decimales) porque así es como vive el precio de
 * Uniswap; la conversión a "cuántos estables por unidad de volátil" se hace al final.
 */

const raizDeTick = (t: number) => Math.pow(1.0001, t / 2);
const tickDeRaiz = (r: number) => Math.log(r * r) / Math.log(1.0001);

export type PlanRescate = {
  /** tick del borde por el que la posición sale convertida a estable */
  tickSalida: number;
  /** los dos bordes del rango, en estables por unidad de volátil (min < max) */
  precioMin: number;
  precioMax: number;
  /** el precio de hoy, para dibujar dónde cae dentro del rango */
  precioHoy: number;
  /** precio al que hay que volver para recuperar el objetivo, en estables por unidad de volátil */
  precioSalida: number;
  /** cuánto tiene que subir el volátil desde hoy, en % */
  subidaPct: number;
  /** ancho total de la banda, en % */
  anchoPct: number;
  /** el tope de estiramiento del vault no da para tanto: la banda se recortará */
  recortado: boolean;
  /** lo que traes, valorado a día de hoy en el estable */
  valorHoy: number;
};

export function planRescate(opciones: {
  tick: number;
  spacing: number;
  /** el estable es token0 del par */
  estableEsToken0: boolean;
  decimalesEstable: number;
  decimalesVolatil: number;
  /** cuánto volátil se trae, en unidades humanas */
  cantidadVolatil: number;
  /** cuánto ESTABLE se trae además, en unidades humanas (0 = solo volátil) */
  cantidadEstable?: number;
  /** cuánto se quiere recuperar, en unidades humanas del estable */
  objetivo: number;
  /** tope de estiramiento del vault, en ticks (`ilRecoverMaxTicks`) */
  topeTicks: number;
}): PlanRescate | null {
  const { tick, spacing, estableEsToken0, decimalesEstable, decimalesVolatil, cantidadVolatil, objetivo, topeTicks } = opciones;
  if (!(cantidadVolatil > 0) || !(objetivo > 0)) return null;

  const s = raizDeTick(tick);
  const amtVol = cantidadVolatil * 10 ** decimalesVolatil;
  const amtEst = (opciones.cantidadEstable ?? 0) * 10 ** decimalesEstable;
  const obj = objetivo * 10 ** decimalesEstable;

  // El borde PEGADO al precio es el primer tick válido del lado por el que aún no se vende; el otro
  // es el de salida. Cuadrarlo a la rejilla del spacing es lo que hará el contrato de todos modos.
  const arriba = Math.ceil(tick / spacing) * spacing + spacing;
  const abajo = Math.floor(tick / spacing) * spacing - spacing;

  // CON ESTABLE, LA BANDA TIENE DOS LADOS. Es la misma estrategia que el contrato llama "financiada"
  // (`RangeMath.recoveryFundedFloor/Ceil`): el volátil llena el lado por el que se vende según el
  // precio se recupera, y el estable llena el de abajo, comprando más volátil si sigue cayendo — un
  // promediado pasivo, sin swap y sin deslizamiento. Sale de tres ecuaciones lineales de Uniswap V3:
  //
  //    amtVol = L·(borde de venta − s)      todo el volátil arriba
  //    amtEst = L·(s − borde de compra)     todo el estable abajo
  //    objetivo = L·(venta − compra)        lo que vale al tocar el borde de venta
  //
  // Sin estable (amtEst = 0) se reduce al caso simple: un solo lado y el borde pegado al precio.
  let tickSalida: number;
  let tickOtro: number;
  let precioHoy: number;
  if (estableEsToken0) {
    // volátil = token1: se vende según BAJA el tick. Salida abajo, compra arriba.
    precioHoy = (1 / (s * s)) * 10 ** (decimalesVolatil - decimalesEstable);
    if (amtEst > 0) {
      if (amtEst >= obj) return null; // el estable ya cubre el objetivo: no hay nada que recuperar
      const a = amtVol / (s * (obj - amtEst));
      const den = obj * a - amtEst * s;
      if (!(a > 0) || a >= s || !(den > 0)) return null;
      const b = amtVol / den;
      if (!(b > s)) return null;
      tickSalida = tickDeRaiz(a);
      tickOtro = tickDeRaiz(b);
    } else {
      const b = raizDeTick(arriba);
      const a = amtVol / (obj * b);
      if (!(a > 0) || a >= s) return null;
      tickSalida = tickDeRaiz(a);
      tickOtro = arriba;
    }
  } else {
    // volátil = token0: se vende según SUBE el tick. Salida arriba, compra abajo.
    precioHoy = s * s * 10 ** (decimalesVolatil - decimalesEstable);
    if (amtEst > 0) {
      if (amtEst >= obj) return null;
      const dif = obj - amtEst;
      const b = (dif * s) / amtVol;            // borde de venta
      const a = (obj * s - amtEst * b) / dif;  // borde de compra
      if (!(b > s) || !(a > 0) || a >= s) return null;
      tickSalida = tickDeRaiz(b);
      tickOtro = tickDeRaiz(a);
    } else {
      const a = raizDeTick(abajo);
      const b = obj / (amtVol * a);
      if (!(b > 0) || b <= s) return null;
      tickSalida = tickDeRaiz(b);
      tickOtro = abajo;
    }
  }

  const rs = raizDeTick(tickSalida);
  const precioSalida = estableEsToken0
    ? (1 / (rs * rs)) * 10 ** (decimalesVolatil - decimalesEstable)
    : rs * rs * 10 ** (decimalesVolatil - decimalesEstable);

  const anchoTicks = Math.abs(tickSalida - tickOtro);
  // El otro borde: pegado al precio si solo se trae volátil, o el suelo de compra si se trae estable.
  const rp = raizDeTick(tickOtro);
  const precioPegado = estableEsToken0
    ? (1 / (rp * rp)) * 10 ** (decimalesVolatil - decimalesEstable)
    : rp * rp * 10 ** (decimalesVolatil - decimalesEstable);

  return {
    tickSalida: Math.round(tickSalida),
    precioMin: Math.min(precioSalida, precioPegado),
    precioMax: Math.max(precioSalida, precioPegado),
    precioHoy,
    precioSalida,
    subidaPct: (precioSalida / precioHoy - 1) * 100,
    anchoPct: (Math.pow(1.0001, anchoTicks) - 1) * 100,
    recortado: anchoTicks > topeTicks,
    valorHoy: cantidadVolatil * precioHoy + (opciones.cantidadEstable ?? 0),
  };
}
